<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable
	content-class="k-rcram-report-a-dialog"
>
<k-card class="k-rcram-report-a-card">
<k-card-title class="k-rcram-title"
	:style="`background-color:${report_banner_bgd_color}`"
>
	<img class="k-rcram-logo" 
		:style="report_logo_style"
		:src="report_logo"
	>
	<div class="k-rcram-report-title"
		v-html="report_title"
	></div>

	<k-spacer/>

	<k-btn color="primary" class="k-rcram-print-btn"
		@click="print_report"
	><k-icon small class="mr-2">fas fa-print</k-icon>Print</k-btn>

	<k-btn color="primary" class="ml-2" 
		v-if="!cram_job_id"
		@click="$emit('dialog_cancel')"
	>Done</k-btn>

</k-card-title>
<k-card-text class="k-rcram-body">
	<div class="k-rcram-h1">
		<div>Resource Set:  <b>{{resource_framework_title}}</b></div>
		<k-spacer/>
		<div>{{comp_label_plural}}:  <a :href="comp_framework_href" target="_blank" style="text-decoration:none"><b>{{comp_framework_title}}</b></a></div>
	</div>

	<div class="mb-5 mt-4 k-rcram-report-header"
		:style="`background-color:${report_banner_bgd_color}`"
	>
		<div v-html="report_intro"></div>
	</div>

	<div class="my-12 text-center" style="font-size:24px"
		v-if="!initialized"
	><b>Loading Report Data…</b></div>

	<!-- TOP-LEVEL RESOURCE BRANCHES -->
	<div class="mb-4 d-flex align-center justify-center k-rcram-report-resource-branches"
		v-if="initialized && resource_branch_item_type"
	>
		<b class="k-rcram-bold-label mr-1">{{resource_branch_item_type_plural}}:</b>
		<div class="k-rcram-resource-branch-title d-flex align-center"
			v-for="(item, index) in resource_branch_items" :key="item.identifier"
			:class="resource_branch_colors[index]"
		>
			<k-checkbox class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" off-icon="far fa-square" on-icon="fas fa-check-square" hide-details
				v-model="resource_branch_showing[index]"
				:color="show_print_view ? '#000' : ''"
				:dark="!show_print_view"
			>
				<template v-slot:label>
					<div :style="show_print_view?'color:#000':'color:#fff'"
						v-html="cfitem_title(item)"
					></div>
				</template>
			</k-checkbox>
		</div>
	</div>

	<!-- OTHER FILTERS -->
	<div class="mb-6 mt-6 align-center justify-center k-rcram-report-filters" style="position:relative"
		v-if="initialized"
	>
		<k-checkbox class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" off-icon="far fa-square" on-icon="fas fa-check-square" hide-details
			v-model="show_branch_columns"
		><template v-slot:label><div style="font-size:20px">Show Alignment Tiers for Individual {{resource_branch_item_type_plural}}</div></template>
		</k-checkbox>

		<k-checkbox class="ml-8 mt-0 pt-0 d-inline-block" style="vertical-align:bottom" off-icon="far fa-square" on-icon="fas fa-check-square" hide-details
			v-model="show_citation_column"
		><template v-slot:label><div style="font-size:20px">Show Citations</div></template>
		</k-checkbox>

		<!-- hidden button to show grid -->
		<div style="position:absolute; left:0; bottom:0; opacity:0">
			<k-btn small @click="view_grid"></k-btn>
		</div>

		<!-- hidden button to toggle showing simscores -->
		<div style="position:absolute; right:0; bottom:0; opacity:0">
			<k-btn small @click="show_simscores=!show_simscores"></k-btn>
		</div>
	</div>

	<!-- ALIGNMENT TIERS -->
	<div class="mb-4 d-flex align-center justify-center"
		v-if="initialized"
	>
		<k-spacer/>
		<b class="k-rcram-bold-label text-right">Alignment Tiers <nobr>({{comp_ids.length}} Standards):</nobr></b>
		<b class="k-rcram-alignment-tier-summary-element"
			v-for="(ad, index) in addressed_categories" :key="index"
			:class="show_print_view ? '' : ad.color"
			:style="comps_addressed_n[ad.index*1] ? '' : 'opacity:0.5'"
		>
			<k-icon class="mr-1"
				:class="show_print_view ? 'black--text' : ad.color"
				>{{ad.icon}}
			</k-icon>
			{{comps_addressed_n[ad.index]}}
			{{ad.text}}
		</b>
		<k-spacer/>

	</div>

	<!-- ========================================================= -->
	<!-- -------------- REPORT A TABLE -------------------------- -->
	<div class="k-rcram-table-outer elevation-5" v-if="initialized">

		<k-data-table light dense
			:headers="table_headers"
			:items="table_rows"
			:items-per-page="-1"
			hide-default-footer
			:height="table_height"
			class="k-rcram-report-table"
		>
			<!-- BODY ROWS -->
	 		<template v-slot:item="{ item }">
				<!-- "Ancestor" rows -->
				<tr class="k-rcram-ancestor-row"
					v-if="item.is_ancestor"
				>
					<td class="k-cram-report-ancestor-comp"
						:id="`td-${item.comp_id}`"
						:colspan="table_header_count"
					>
						<div class="d-flex align-center"
							v-html="item.comp_text"
						></div>
					</td>
				</tr>

				<tr v-else>
					<!-- Competency -->
					<td class="k-rcram-comp-cell"
						:id="`td-${item.comp_id}`"
					>
						<div class="my-2"
							v-html="item.comp_text"
						></div>
					</td>

					<!-- "Addressed" -->
					<td class="k-rcram-addressed-cell"
						style="border-left:1px solid #666!important"
					>
						<div class="k-rcram-addressed-cell-div" style="border-radius:8px"
							:class="`${item.addressed_bgd_color} ${item.addressed_color}`"
						>
							<k-icon class="k-rcram-addressed-cell-icon"
								:class="show_print_view ? 'black--text': item.addressed_color"
							>{{item.addressed_icon}}</k-icon>
							<b class="k-rcram-addressed-cell-label">{{item.addressed_text}}</b>
						</div>
					</td>

					<!-- columns for each branch -->
					<td class="k-rcram-addressed-branch-cell"
						v-for="(ao, branch_id) in item.ao_branches"
						v-if="show_branch_columns"
						:key="branch_id"
						:class="show_print_view ? '' : `${ao.bgd_color}`"
						style="border-left:1px solid #666!important; border-bottom:1px solid #ccc!important"
					>
						<k-icon
							:class="show_print_view ? 'black--text' : ao.color"
						>{{ao.icon}}</k-icon>
					</td>

					<!-- Citations -->
					<td class="k-rcram-citations-cell"
						v-if="show_citation_column"
						style="border-left:1px solid #666!important"
						:style="citations_cell_style"
					>
						<div class="d-flex align-center"
							v-if="item.all_citations && item.all_citations.length > 0"
						>
							<div v-html="item.citation_text"></div>
							<k-spacer/>
							<div class="k-rcram-show-all-citations-btn">
								<k-tooltip bottom><template v-slot:activator="{on}">
									<k-btn small v-on="on" icon color="#666" aria-label="Show all citations"
										@click="show_all_citations(item)"
									><k-icon>fas fa-circle-ellipsis</k-icon></k-btn>
								</template>Show all citations ({{item.all_citations.length}})</k-tooltip>
							</div>
						</div>
					</td>
				</tr>
			</template>

		</k-data-table>
	</div>

</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import SCRAMReportMixin from './SCRAMReportMixin.js'

export default {
	mixins: [SCRAMReportMixin,],
	props: {
		// req: { type: String, required: true },
		cram_job_id: { type: String, required: false, default() { return '1001'} },
	},
	data() { return {
		dialog_open: true,
		show_print_view: false,

		resource_ids_for_report: new Set(),

		addressed_categories: [
			{
				index: 0,
				text: 'Comprehensive',
				icon: 'fas fa-circle-dot',
				color: 'green--text text--darken-3',
				bgd_color: 'green lighten-5',
			},
			{
				index: 1,
				text: 'Robust',
				icon: 'fas fa-circle',
				color: 'green--text text--darken-3',
				bgd_color: 'green lighten-5',
			},
			{
				index: 2,
				text: 'Emergent',
				icon: 'fas fa-circle-half-stroke',
				color: 'blue--text text--darken-3',
				bgd_color: 'blue lighten-5',
			},
			{
				index: 3,
				text: 'Minimal',
				icon: 'far fa-circle',
				color: 'amber--text text--darken-3',
				bgd_color: 'amber lighten-5',
			},
			{
				index: 4,
				text: 'Not Aligned',
				icon: 'fas fa-circle-minus',
				color: 'red--text text--darken-3',
				bgd_color: 'red lighten-5',
			},
		],
		comps_addressed_n: [0, 0, 0, 0, 0],	// one entry per addressed_categories item

		// addressed_categories: [
		// 	{
		// 		index: 0,
		// 		text: 'Yes',
		// 		icon: 'fas fa-circle',
		// 		color: 'green--text text--darken-3',
		// 		bgd_color: 'green lighten-5',
		// 	},
		// 	{
		// 		index: 1,
		// 		text: 'Partial',
		// 		icon: 'fas fa-circle-half-stroke',
		// 		color: 'blue--text text--darken-3',
		// 		bgd_color: 'blue lighten-5',
		// 	},
		// 	{
		// 		index: 2,
		// 		text: 'Minimal',
		// 		icon: 'far fa-circle',
		// 		color: 'amber--text text--darken-3',
		// 		bgd_color: 'amber lighten-5',
		// 	},
		// 	{
		// 		index: 3,
		// 		text: 'No',
		// 		icon: 'fas fa-circle-minus',
		// 		color: 'red--text text--darken-3',
		// 		bgd_color: 'red lighten-5',
		// 	},
		// ],

		// use these labels and thresholds for our alignment categories in this report
		alignment_categories: [
			{
				alignment_category: 6,
				// label: 'Highly Likely Direct Alignment',
				// threshold: 80,
				// threshold: 90,
				label: 'Highly Comprehensive',
				threshold: 95,
			},
			{
				alignment_category: 5,
				// label: 'Likely Direct Alignment',
				// threshold: 65,
				// threshold: 75,
				label: 'Comprehensive',
				threshold: 80,
			},
			{
				alignment_category: 4,
				// label: 'Likely Partial Support',
				// threshold: 50,
				// threshold: 50,
				label: 'Robust',
				threshold: 60,
			},
			{
				alignment_category: 3,
				// label: 'Possible Contextual Relevance',
				// threshold: 35,
				// threshold: 35,
				label: 'Emergent',
				threshold: 40,
			},
			{
				alignment_category: 2,
				// label: 'Minimal/Surface Relevance',
				// threshold: 20,
				// threshold: 20,
				label: 'Minimal',
				threshold: 25,
			},
			{
				alignment_category: 1,
				// label: 'Not Relevant',
				// threshold: 0,
				// threshold: 0,
				label: 'Not Relevant',
				threshold: 0,
			},
		],
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),

		initialized() {
			return this.matrix && this.rfr && this.cfr
		},

		table_height() {
			if (this.show_print_view) return ''
			return 'calc(100vh - 80px)'
		},

		show_simscores: {
			get() { return this.$store.state.lst.scram_show_simscores },
			set(val) { this.$store.commit('lst_set', ['scram_show_simscores', val]) }
		},

		show_branch_columns: {
			get() { return this.$store.state.lst.scram_show_branch_columns },
			set(val) { this.$store.commit('lst_set', ['scram_show_branch_columns', val]) }
		},

		show_citation_column: {
			get() { return this.$store.state.lst.scram_show_citation_column },
			set(val) { this.$store.commit('lst_set', ['scram_show_citation_column', val]) }
		},

		citations_cell_style() {
			if (this.show_branch_columns) return 'width:25%'
			else return 'width:33%'
		},

		resource_ids_by_branch() {
			if (this.resource_branch_items.length == 0) return {}

			let matrix = this.matrix
			if (!empty(this.sub_matrix)) matrix = this.sub_matrix

			let o = {}
			for (let item of this.resource_branch_items) {
				o[item.identifier] = new Set()
			}

			// for the first comp...
			for (let comp_id in matrix) {
				// for each resource_id
				for (let resource_id in matrix[comp_id]) {
					// check each branch
					for (let branch_id in o) {
						// if this branch is an ancestor of the resource_id, add it to the branch's set
						if (U.trace_up_to_ancestor(this.rfr, resource_id, branch_id, 'id')) {
							o[branch_id].add(resource_id)
							// an item is only in one branch, so break when we find it
							break
						}
					}
				}
				// we only go through the first comp
				break
			}

			return o
		},

		// 
		branch_from_resource_id_hash() {
			let o = {}
			let i = 0
			for (let branch_identifier in this.resource_ids_by_branch) {
				for (let resource_id of this.resource_ids_by_branch[branch_identifier]) {
					o[resource_id] = {
						branch_identifier: branch_identifier,
						branch_index: i
					}
				}
				++i
			}
			return o
		},

		// not currently used
		// n_resources() { return this.resource_ids_for_report.length },

		comp_ids() {
			if (!this.initialized) return []

			let arr = []
			for (let comp_id in this.matrix) {
				arr.push(comp_id)
			}

			return arr
		},
		n_comps() { return this.comp_ids.length },

		table_headers() {
			let arr = [
				{ text: this.comp_label_plural, align:'left', sortable: false, value: 'comp_text' },
				{ text: 'Curriculum-Wide Alignment Tier', align:'center', class: 'k-rcram-addressed-cell-header', sortable: false, value: 'addressed_text' },
			]

			if (this.show_branch_columns) {
				for (let i = 0; i < this.resource_branch_showing.length; ++i) {
					if (this.resource_branch_showing[i]) {
						let branch_item = this.resource_branch_items[i]
						let heading = branch_item.fullStatement
						arr.push({ text: heading, align:'center', class: 'k-rcram-addressed-branch-cell', sortable: false, value: 'addressed_text' })
					}
				}
			}
			if (this.show_citation_column) {
				arr.push({ text: 'Citations', align:'left', sortable: false, value: 'citation_text' })
			}
			return arr
		},

		table_header_count() {
			return this.table_headers.length
		},

		table_rows() {
			if (!this.initialized) return []

			let arr = []

			// one row per comp
			let ancestors, last_ancestors
			let i = 0
			let comps_addressed_n = [0, 0, 0, 0, 0]	// calculate comps_addressed_n while we calculate table_rows
			for (let comp_id of this.comp_ids) {
				// get ancestors of this comp
				ancestors = this.get_ancestors(comp_id)

				// if these are different than the last ancestors...
				if (JSON.stringify(ancestors?.map(x=>x?.identifier)) != JSON.stringify(last_ancestors?.map(x=>x?.identifier))) {
					let comp_text = ''
					let a_cfi
					// go through each ancestor, from the most distant to most recent one
					for (let j = 0; j < ancestors.length; ++j) {
						a_cfi = ancestors[j]

						let item_comp_text = this.cfitem_title(a_cfi)
						if (j > 0) {
							item_comp_text = item_comp_text.replace(/<p>/, `<p class="k-cram-report-ancestor-comp-component">`)
						}
						comp_text += item_comp_text
					}

					arr.push({
						is_ancestor: true,

						// use the last (least-distant) ancestor as the comp_id/c_cfi
						comp_id: a_cfi.identifier,
						comp_sequence: i,
						comp_text: comp_text,
						c_cfi: a_cfi,
					})
				}

				++i
				let c_cfi = this.comp_cfitems[comp_id]
				////////////////
				// this is where we use the algorithm to determine a) how well the comp is addressed; b) "citations" to show...

				// ... for all top level branches that we're showing...
				let ao = this.get_addressed(comp_id, comps_addressed_n)

				// and for each of the top level branches currently selected
				let ao_branches = {}
				for (let i = 0; i < this.resource_branch_showing.length; ++i) {
					if (this.resource_branch_showing[i]) {
						let branch_id = this.resource_branch_items[i].identifier
						ao_branches[branch_id] = this.get_addressed(comp_id, null, this.resource_ids_by_branch[branch_id])
					}
				}

				let o = {
					// info about the comp itself
					comp_id: comp_id,
					comp_sequence: i,
					comp_text: this.cfitem_title(c_cfi),
					c_cfi: c_cfi,

					// addressed
					addressed_index: ao.index,
					addressed_text: ao.text,
					addressed_icon: ao.icon,
					addressed_color: ao.color,
					addressed_bgd_color: ao.bgd_color,

					// citations
					citation_text: ao.citation_text,
					all_citations: ao.all_citations,

					ao: ao,
					ao_branches: ao_branches,
				}

				arr.push(o)
				
				last_ancestors = ancestors
			}

			this.comps_addressed_n = comps_addressed_n

			return arr
		},

		report_title() {
			// TODO: store this as part of the job
			if (this.is_bju_report) return `<b>BJU Press</b> Educational Standards Correlation and Comparison Report`
			return `<b>Rosetta Align:</b> Standards Alignment Report`
		},
	},

//////////////////////////////////////////
	watch: {
		// we have to manage resource_ids_for_report this way to make reactivity work well
		resource_branch_showing: { deep: true, handler() {
			this.calculate_resource_ids_for_report()
		}}
	},

/////////////////////////////////////////
	async created() {
		vapp.scram_report_a_view = this
		await this.load_data()

		// calculate alignment_category matrix items using our algorithm
		this.calculate_matrix_alignment_categories(this.matrix)
		if (!empty(this.sub_matrix)) this.calculate_matrix_alignment_categories(this.sub_matrix)

		// set page title
		document.title = U.html_to_text(this.report_title)

		// intercept cmd-P and use our print_report function instead (Chrome does not allow us to intercept the menu function)
		window.addEventListener('keydown', (event) => {
			let is_print_shortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p'
			if (is_print_shortcut) {
				event.preventDefault()
				this.print_report()
			}
		})
	},
	mounted() {
	},

/////////////////////////////////////////
	methods: {
		// compute the relevant alignment stats for this comp
		get_addressed(comp_id, comps_addressed_n, resource_ids_to_check = this.resource_ids_for_report) {
			// determine if this standard has been addressed...
			let matrix = this.matrix
			if (!empty(this.sub_matrix)) matrix = this.sub_matrix

			let matrix_row = sc(matrix[comp_id])

			let all_cells = []
			let comprehensive_cells = []
			let robust_cells = []
			let emergent_cells = []
			let minimal_cells = []

			let highest_cell = null
			let highest_alignment_category = null

			// for each resource...
			for (let resource_id in matrix_row) {
				// *** skip resources that aren't currently selected
				if (!resource_ids_to_check.has(resource_id)) continue

				let cell = matrix_row[resource_id]
				cell.comp_id = comp_id
				cell.resource_id = resource_id

				all_cells.push(cell)

				// record highest simscore/alignment_category
				if (empty(highest_cell) || cell.simscore > highest_cell.simscore) {
					highest_cell = cell
					highest_alignment_category = cell.alignment_category
				}

				// alignment_category 1: that's a no (we don't record these cells for local use)
				if (cell.alignment_category == 1) {

				// alignment_category 2: minimal
				} else if (cell.alignment_category == 2) {
					minimal_cells.push(cell)

				// alignment_category 3: emergent
				} else if (cell.alignment_category == 3) {
					emergent_cells.push(cell)

				// alignment_category 4: robust
				} else if (cell.alignment_category == 4) {
					robust_cells.push(cell)

				// alignment_category 6 or 5: comprehensive
				} else {
					comprehensive_cells.push(cell)
				}
			}

			// sort all cell arrays by simscore descending
			all_cells.sort((a,b)=>b.simscore-a.simscore)
			comprehensive_cells.sort((a,b)=>b.simscore-a.simscore)
			robust_cells.sort((a,b)=>b.simscore-a.simscore)
			emergent_cells.sort((a,b)=>b.simscore-a.simscore)
			minimal_cells.sort((a,b)=>b.simscore-a.simscore)

			let rv, examples

			// if we found at least one comprehensive or 3 robust, 'Comprehensive'
			if (comprehensive_cells.length > 0 || robust_cells.length >= 3) {
				rv = sc(this.addressed_categories[0])

				// use top 3 sections as example citations
				examples = all_cells.slice(0,3)

				// use a star if the highest_alignment_category is 6, but only for individual branches
				if (resource_ids_to_check != this.resource_ids_for_report) {
					if (highest_alignment_category == 6) {
						rv.icon = 'fas fa-circle-star'
					}
				}

			// if we found at least one robust or 3 emergent, 'Robust'
			} else if (robust_cells.length > 0 || emergent_cells.length >= 3) {
				rv = sc(this.addressed_categories[1])

				// use top 3 sections as example citations
				examples = all_cells.slice(0,3)

			// if we found at least one emergent or 3 minimal, 'Emergent'
			} else if (emergent_cells.length > 0 || minimal_cells.length >= 3) {
				rv = sc(this.addressed_categories[2])

				// use top 3 sections as example citations
				examples = all_cells.slice(0,3)

			// if we found at least one minimal, 'Minimal'
			} else if (minimal_cells.length > 0) {
				rv = sc(this.addressed_categories[3])

				// only cite the first section?
				examples = minimal_cells.slice(0,1)
			
			// else 'no'
			} else {
				rv = sc(this.addressed_categories[4])

				// no citation? or we could site the highest cell
				examples = []
				// examples = [highest_cell]
			}

			// format example citations into citation_text
			let s = ''
			for (let ex of examples) {
				let ritem = this.rfr.cfo.cfitems[ex.resource_id]
				if (empty(ritem)) s += '<p>???</p>'	// shouldn't happen
				else {
					let cfi_s = this.cfitem_title({
						humanCodingScheme: ritem.humanCodingScheme,
						fullStatement: ritem.fullStatement 
							+ (this.show_simscores ? ` [${ex.simscore}]` : '')
					}).trim()

					// add an onclick to each cfitem p tag to show the summary of the citation
					// (as long as it has an extension.description, which it always should)
					if (!empty(ritem.extensions?.description)) {
						cfi_s = cfi_s.replace(/<p\b/, `<p onclick="vapp.scram_report_a_view.show_citation('${ex.resource_id}')"`)
					}

					// add a class for the citation's branch, except in print view
					if (!this.show_print_view) {
						let o = this.branch_from_resource_id_hash[ritem.identifier]
						if (!empty(o)) {
							let c = this.resource_branch_colors[o.branch_index]
							c = c.replace(/ (darken)/, '--text text--$1')
							cfi_s = cfi_s.replace(/<p\b/, `<p class="${c}"`)
						}
					}

					s += cfi_s
				}
			}
			rv.citation_text = s
			rv.n_example_citations = examples.length

			rv.highest = highest_cell

			// now get a list of all the citations to show if user clicks to see all
			rv.all_citations = []
			for (let i = 0; i < all_cells.length; ++i) {
				let cell = all_cells[i]
				// add all example citations
				if (i < examples.length) {
					rv.all_citations.push(cell)

				// for addresssed_data index 4 (Not Aligned), never show any more
				} else if (rv.index == 4) {
					break

				// for addressed_categories index 3 (Minimal/Emergent), only show examples down to the minimal/surface alignments
				} else if (rv.index == 3 || rv.index == 2) {
					if (cell.alignment_category >= 2) {
						rv.all_citations.push(cell)
					} else {
						// break once we get beyond the minimal alignments
						break
					}

				// for addressed_categories index 1 or 2 (Comprehensive/Robust), show down to the emergent alignments (alignment_category 3)
				} else {
					if (cell.alignment_category >= 3) {
						rv.all_citations.push(cell)
					} else {
						// break once we get beyond the emergent alignments
						break
					}
				}
			}
			// rv.citation_text += `{${rv.all_citations.length}}`

			// compile comps_addressed_n
			if (comps_addressed_n) {
				comps_addressed_n[rv.index] += 1
			}

			return rv
		},

		show_citation(resource_id) {
			let ritem = this.rfr.cfo.cfitems[resource_id]

			let text = `<div class="k-rcram-citation-detail-title">`
				+ this.cfitem_title(ritem)
				+ `</div>`
				+ `<div class="k-rcram-citation-detail-text">`
				// TODO: customize this "description of the description"
				+ `<b>Summary:</b> `
				// note: this fn should not be called if we don't have extensions.description for the resource
				+ ritem.extensions.description
				+ `</div>`
			
			this.$alert({
				text: text,
				acceptText: 'OK  ',
				acceptIcon: 'fas fa-circle-check',
				dialogMaxWidth: 640,
			})
		},

		show_all_citations(row_item) {
			let s = row_item.comp_text
				+ '<div class="k-rcram-all-citations-list">'
			
			for (let cell of row_item.all_citations) {
				let ritem = this.rfr.cfo.cfitems[cell.resource_id]
				if (empty(ritem)) s += '<p>???</p>'	// shouldn't happen
				else {
					let cfi_s = this.cfitem_title({
						humanCodingScheme: ritem.humanCodingScheme,
						fullStatement: ritem.fullStatement 
							+ (this.show_simscores ? ` [${cell.simscore}]` : '')
					}).trim()

					// add an onclick to each cfitem p tag to show the summary of the citation
					// (as long as it has an extension.description, which it always should)
					if (!empty(ritem.extensions?.description)) {
						cfi_s = cfi_s.replace(/<p\b/, `<p onclick="vapp.scram_report_a_view.show_citation('${cell.resource_id}')"`)
					}

					// add a class for the citation's branch
					let o = this.branch_from_resource_id_hash[ritem.identifier]
					if (!empty(o)) {
						let c = this.resource_branch_colors[o.branch_index]
						c = c.replace(/ (darken)/, '--text text--$1')
						cfi_s = cfi_s.replace(/<p\b/, `<p class="${c}"`)
					}

					// put the alignment_category icon to the left of the citation
					let icon, color
					let i = this.addressed_categories.length - cell.alignment_category
					if (i == -1) {
						icon = 'fas fa-circle-star'
						color = this.addressed_categories[0].color
					} else {
						icon = this.addressed_categories[i].icon
						color = this.addressed_categories[i].color
					}

					s += `<div class="d-flex">
						<i class="mr-2 ${icon} ${color}" style="font-size:22px;"></i>
						${cfi_s}
						</div>
					`
				}
			}
			s += '</div>'

			this.$alert({
				title: 'All Citations',
				text: s,
				acceptText: 'OK  ',
				acceptIcon: 'fas fa-circle-check',
				dialogMaxWidth: 800,
			})
		},

		calculate_alignment_category(simscore) {
			for (let rc of this.alignment_categories) {
				if (simscore >= rc.threshold) return rc.alignment_category
			}
			return this.alignment_categories.length-1
		},

		calculate_matrix_alignment_categories(matrix) {
			// calculate and replace the alignment_category values for all items in the matrix
			for (let comp_id in matrix) {
				for (let resource_id in matrix[comp_id]) {
					let cell = matrix[comp_id][resource_id]
					cell.alignment_category = this.calculate_alignment_category(cell.simscore)
				}
			}
		},

		// calculate the resource_ids we're currently showing in the report
		// doing this with a computed makes reactivity of the table work poorly
		calculate_resource_ids_for_report() {
			if (!this.initialized) {
				this.resource_ids_for_report = new Set([])
				return
			}

			let matrix = this.matrix
			if (!empty(this.sub_matrix)) matrix = this.sub_matrix

			// determine if we need to check branches at all; save time if we don't have to
			let check_branches = false
			if (this.resource_branch_showing.length > 0) {
				for (let i = 0; i < this.resource_branch_showing.length; ++i) {
					if (!this.resource_branch_showing[i]) {
						check_branches = true
						break
					}
				}
			}

			let arr = new Set([])
			for (let comp_id in matrix) {
				for (let resource_id in matrix[comp_id]) {
					// if we are using resource_branch_showing and something is unchecked...
					if (check_branches) {
						// only show resources in the selected branches
						let in_branch = false
						for (let i = 0; i < this.resource_branch_showing.length; ++i) {
							// branch isn't showing: continue
							if (!this.resource_branch_showing[i]) continue

							// get the identifier for the branch
							let branch_id = this.resource_branch_items[i].identifier
							// if resource is in this branch, add it
							if (this.resource_ids_by_branch[branch_id].has(resource_id)) {
								in_branch = true
								break
							}
						}

						// if not in a branch, skip the resource
						if (!in_branch) continue
					}

					// if we get to here, add the resource_id
					arr.add(resource_id)
				}
				
				// we only go through the first comp
				break
			}

			this.resource_ids_for_report = arr
		},

		view_grid() {
			window.open(`/crm/${this.cram_job_id}/grid`)
		},

		view_comp(id) {
			window.open(`/crm/${this.cram_job_id}/comp/${id}/0`)
		},

		view_resource(id) {
			window.open(`/crm/${this.cram_job_id}/resource/${id}/0`)
		},

		print_report() {
			// get a jquery reference to the dialog
			let dialog = $('.k-rcram-report-a-dialog')

			// add class to dialog and its parent: in addition to allowing us to customize styles of parts of the dialog,
			// we also set css on the dialog/parent themselves that makes printing work
			dialog.addClass('k-rcram-report-a-dialog-print')
			dialog.parent().addClass('k-rcram-report-a-dialog-parent-print')

			this.show_print_view = true

			// return here for debugging -- to see what the print-view version looks like
			// return

			this.$nextTick(x=>{
				// Clean up after printing
				window.addEventListener('afterprint', x=>{
					dialog.removeClass('k-rcram-report-a-dialog-print')
					dialog.parent().removeClass('k-rcram-report-a-dialog-parent-print')
					this.show_print_view = false
				})

				window.print()
			})
		}
	}
}
</script>

<style lang="scss">
// note that styles are in ResourceMatrixView
</style>